import { gql } from '@apollo/client'
import { Button, Caption, Chip } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useCallback, useEffect, useState } from 'react'

import { useRegisterLockEvent } from '../../hooks'
import { LockEventName } from '../../types'
import { JusAILogo } from '../ai-offer-lock/jus-ai-logo'
import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { FrequencyToggle } from '../frequency-toggle'
import { OneClickButton } from '../one-click-button'
import {
  AiOfferSwitchFrequencyLockContent_OfferLockDataFragment,
  AiOfferSwitchFrequencyLockContent_ViewerFragment,
} from './__generated__/ai-offer-switch-frequency-lock-content.graphql'
import styles from './ai-offer-switch-frequency-lock.module.scss'

export interface AiOfferSwitchFrequencyLockContentProps {
  offerLockData: AiOfferSwitchFrequencyLockContent_OfferLockDataFragment
  viewer?: AiOfferSwitchFrequencyLockContent_ViewerFragment | null
}

const isYearlyAndHasCreditCard = (
  card: AiOfferSwitchFrequencyLockContent_OfferLockDataFragment['body']['cards'][0]
) => !!card.offer.installmentsText && !!card.creditCard

export function AiOfferSwitchFrequencyLockContent({
  offerLockData,
  viewer,
}: AiOfferSwitchFrequencyLockContentProps) {
  const { registerLockEvent } = useRegisterLockEvent()
  const { body, header } = offerLockData || {}
  const [currentUrl, setCurrentUrl] = useState('')
  const { toggle } = header || {}
  const { cards } = body || {}

  const { description, options = [], defaultOption } = toggle || {}
  const [monthlyFrequency, yearlyFrequency] = options
  const [currentFrequency, setCurrentFrequency] = useState<string>(defaultOption)
  const [currentCard, setCurrentCard] = useState(
    defaultOption === monthlyFrequency ? cards[0] : cards[1]
  )

  useEffect(() => {
    setCurrentUrl(window.location.href)
  }, [])

  const isPromotional = !!currentCard?.offer?.priceAfterPromotionalPeriodText
  const href = `https://www.jusbrasil.com.br/login?next_url=${encodeURIComponent(currentUrl)}`
  const buttonLabel = currentCard?.button?.label || 'Assinar agora'

  const getCardsContent = useCallback(
    (frequency: string) =>
      frequency === monthlyFrequency ? setCurrentCard(cards[0]) : setCurrentCard(cards[1]),
    [monthlyFrequency, cards]
  )

  const sendFrequencyToggleClickedEvent = useCallback(
    (card: AiOfferSwitchFrequencyLockContent_OfferLockDataFragment['body']['cards'][0]) => {
      const { lockEventProperties } = card
      if (!lockEventProperties) return

      const { experiment_id, feature, current_plan, next_plan, from_component, pid } =
        lockEventProperties
      sendMetrics('Box.FrequencyToggleClicked', pid ? String(pid) : null, {
        experiment_id,
        feature,
        current_plan,
        next_plan,
        from_component,
      })
    },
    []
  )

  function handleChangeFrequency(frequency: string): void {
    const nextCard = frequency === monthlyFrequency ? cards[0] : cards[1]

    if (nextCard?.lockEventProperties) {
      sendFrequencyToggleClickedEvent(nextCard)
    }

    getCardsContent(frequency)
    registerLockEvent({
      eventName: LockEventName.LOCK_VIEW,
      eventProperties: [currentCard.lockEventProperties],
    })
    setCurrentFrequency(frequency)
  }

  return (
    <>
      <JusAILogo width={80} height={22} />
      <div className={styles['ai-offer-switch-frequency-lock-header']}>
        <FrequencyToggle
          options={options}
          currentFrequency={currentFrequency}
          onChangeFrequency={handleChangeFrequency}
        />
        <div className={styles['ai-offer-switch-frequency-lock-toggle-row-info']}>
          <Caption className={styles['ai-offer-switch-frequency-lock-toggle-row-info-caption']}>
            {description?.text}
          </Caption>
          <Chip size="md" kind="secondary">
            <strong>{description?.benefit}</strong>
          </Chip>
        </div>

        {currentCard?.offer?.priceText && !isPromotional && (
          <Price align="center" size="md">
            {currentCard?.offer?.priceTitle && (
              <Price.Discount>
                {currentCard?.offer?.installmentsText ? (
                  <span data-testid="price-label">
                    {currentCard?.offer?.priceTitle}
                    <strong className={styles['ai-offer-switch-frequency-lock-price-installments']}>
                      {' '}
                      {currentCard?.offer?.installmentsText}
                    </strong>{' '}
                    de:
                  </span>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: currentCard?.offer?.priceTitle }} />
                )}
              </Price.Discount>
            )}
            <Price.Value>
              {currentCard?.offer?.priceText}
              {currentCard?.offer?.frequency && currentFrequency !== yearlyFrequency && (
                <Price.ValueFrequency>{currentCard?.offer?.frequency}</Price.ValueFrequency>
              )}
            </Price.Value>
          </Price>
        )}
        {currentCard?.offer?.priceText && isPromotional && (
          <Price align="center" size="md">
            <Price.Value>{currentCard.title}</Price.Value>
            {currentCard?.offer?.priceTitle && (
              <Price.Discount>
                {currentCard?.offer?.installmentsText ? (
                  <span data-testid="price-label">
                    <strong className={styles['ai-offer-switch-frequency-lock-price-installments']}>
                      {currentCard?.offer?.installmentsText}
                    </strong>{' '}
                    {currentCard?.offer?.priceTitle}
                  </span>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: currentCard?.offer?.priceTitle }} />
                )}
              </Price.Discount>
            )}
          </Price>
        )}
      </div>
      <BenefitsList
        offerLockCard={currentCard}
        benefitsListClassName={styles['ai-offer-switch-frequency-lock-benefits']}
      />
      <div className={styles['ai-offer-switch-frequency-lock-card']}>
        {viewer ? (
          <>
            {isYearlyAndHasCreditCard(currentCard) ? (
              <>
                <CheckoutButton offerLockCard={currentCard} kind="primary" />
                <CheckoutButton
                  offerLockCard={{
                    ...currentCard,
                    creditCard: {
                      ...currentCard.creditCard,
                      ccToken: null,
                    } as AiOfferSwitchFrequencyLockContent_OfferLockDataFragment['body']['cards'][0]['creditCard'],
                  }}
                  kind="primary"
                />
              </>
            ) : (
              <>
                <CheckoutButton offerLockCard={currentCard} kind="primary" />
                <OneClickButton offerLockCard={currentCard} viewer={viewer} kind="primary" />
              </>
            )}
          </>
        ) : (
          <>
            <Button kind="primary" asChild>
              <a href={href}>{buttonLabel}</a>
            </Button>
          </>
        )}
      </div>
    </>
  )
}

AiOfferSwitchFrequencyLockContent.fragments = {
  offerLockData: gql`
    fragment AiOfferSwitchFrequencyLockContent_offerLockData on OfferLockData {
      header {
        toggle {
          defaultOption
          options
          description {
            text
            benefit
          }
        }
      }
      body {
        cards {
          title
          offer {
            priceTitle
            priceText
            frequency
            installmentsText
            priceAfterPromotionalPeriodText
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
          ...BenefitsList_offerLockCard
        }
      }
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment AiOfferSwitchFrequencyLockContent_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
    ${BenefitsList.fragments.offerLockCard}
  `,
}
